import { Box, Drawer, useTheme } from '@mui/material'
import ExpandCollapseButton from 'components/ui/expand-collapse-button'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

export interface DrawerLayoutProps {
  /** Whether the drawer is open */
  open: boolean
  /** Callback when drawer should close */
  onClose: () => void
  /** Content to show when header is collapsed */
  collapsedHeader: ReactNode
  /** Content to show when header is expanded */
  expandedHeader: ReactNode
  /** Main content (typically tabs + panels) */
  children: ReactNode
  /** Drawer width as percentage or pixels (default: '74%') */
  width?: string | number
  /** @deprecated Close button is now inside drawer in top-right corner */
  closeButtonLeft?: number
  /** Whether to close on ESC key (default: true) */
  closeOnEsc?: boolean
  /** Whether to close on click outside (default: true) */
  closeOnClickOutside?: boolean
  /** Initial expanded state (default: false) */
  defaultExpanded?: boolean
  /** Controlled expanded state */
  expanded?: boolean
  /** Callback when expanded state changes */
  onExpandedChange?: (expanded: boolean) => void
  /** Custom Paper sx props */
  paperSx?: object
}

const DrawerLayout: React.FC<DrawerLayoutProps> = ({
  open,
  onClose,
  collapsedHeader,
  expandedHeader,
  children,
  width = '74%',
  closeButtonLeft = 350,
  closeOnEsc = true,
  closeOnClickOutside = true,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onExpandedChange,
  paperSx,
}) => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const paperRef = useRef<HTMLDivElement | null>(null)

  // Manage expanded state (controlled or uncontrolled)
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded)
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded

  const handleExpandedChange = useCallback(
    (newExpanded: boolean) => {
      if (controlledExpanded === undefined) {
        setInternalExpanded(newExpanded)
      }
      onExpandedChange?.(newExpanded)
    },
    [controlledExpanded, onExpandedChange]
  )

  // Handle close with cleanup
  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  // ESC key and click outside handlers
  useEffect(() => {
    if (!open) return

    const isClickInsideDrawer = (node: Node) => !!paperRef.current && paperRef.current.contains(node)

    const isGridRoot = (el: Element | null) => !!el && !!el.closest('.global-data-grid')

    const isRowHeaderOrCell = (el: Element | null) =>
      !!el &&
      !!el.closest('.MuiDataGrid-row, .MuiDataGrid-cell, .MuiDataGrid-columnHeaders, .MuiDataGrid-toolbarContainer')

    const onEsc = (e: KeyboardEvent) => {
      if (!closeOnEsc) return
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      handleClose()
    }

    const onDocClick = (e: MouseEvent) => {
      if (!closeOnClickOutside) return
      const target = e.target as Element | null
      // Don't close if clicking inside the drawer
      if (target && isClickInsideDrawer(target)) {
        return
      }

      // Don't close if clicking on grid rows/cells (user might be selecting data)
      if (target && isRowHeaderOrCell(target)) {
        return
      }

      // Close for clicks anywhere else (including non-cell grid areas, backdrop, etc.)
      // handleClose()
    }

    window.addEventListener('keydown', onEsc, true)
    window.addEventListener('click', onDocClick, true)

    return () => {
      window.removeEventListener('keydown', onEsc, true)
      window.removeEventListener('click', onDocClick, true)
    }
  }, [open, closeOnEsc, closeOnClickOutside, handleClose])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      variant="persistent"
      hideBackdrop
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        ref: paperRef,
        sx: {
          display: 'flex',
          flexDirection: 'column',
          width,
          height: '100dvh',
          pt: pinned ? '55px' : 0,
          overflow: 'hidden',
          bgcolor: theme.palette.background.section,
          boxShadow: '10px 21px 43px rgba(0, 0, 0, 0.2)',
          zIndex: theme => {
            return 1
            return theme.zIndex.drawer
          },
          transition: 'padding 0.3s ease',
          ...paperSx,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <ExpandCollapseButton expanded={isExpanded} setExpanded={handleExpandedChange} displayElement={collapsedHeader}>
          {expandedHeader}
        </ExpandCollapseButton>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{children}</Box>
      </Box>
    </Drawer>
  )
}

export default DrawerLayout
